import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { SearchFilters, ProviderSearchResult, ProviderSearchSessionMetadata } from "../types/providerSearch";
import {
  DEFAULT_SEARCH_FILTERS,
  PAGINATION_CONFIG,
  STORAGE_LIMITS,
} from "../utils/constants";
import {
  hasMoreCumulativePage,
  sliceCumulativePage,
} from "../utils/paginationUtils";
import { migrateLegacySearchFilters } from "../constants/providerSearch";
import { normalizeSearchFilters } from "../utils/searchFilterUtils";

// Store state interface
interface ProviderState {
  // Provider data
  allProviders: ProviderSearchResult[];
  filteredProviders: ProviderSearchResult[];
  paginatedProviders: ProviderSearchResult[];

  // Search state
  searchFilters: SearchFilters;
  searchCenter: { lat: number; lng: number } | null;
  searchRadius: number | null;
  searchSessionMetadata: ProviderSearchSessionMetadata | null;

  // UI state
  loading: boolean;
  error: string | null;
  hasMoreResults: boolean;

  // User interactions
  savedProviders: Set<number>;
  comparedProviders: Set<number>;
  /**
   * In-memory snapshots for compare slots (not persisted). Survives ProviderListPage
   * remounts within the SPA session so prune does not wipe selections after
   * opening a profile and returning.
   */
  stagedCompareProviders: ProviderSearchResult[];

  // Pagination
  currentPage: number;
  itemsPerPage: number;

  // Auto-search flags
  hasAutoSearched: boolean;
  hasSearchedWithZeroResults: boolean;
}

// Store actions interface
interface ProviderActions {
  // Provider data actions
  setAllProviders: (providers: ProviderSearchResult[]) => void;
  setFilteredProviders: (providers: ProviderSearchResult[]) => void;
  setPaginatedProviders: (providers: ProviderSearchResult[]) => void;

  // Search actions
  setSearchFilters: (filters: SearchFilters) => void;
  updateSearchFilter: (key: keyof SearchFilters, value: unknown) => void;
  /** Merge several filter keys in one write (avoids multi-lane Care Services races). */
  patchSearchFilters: (partial: Partial<SearchFilters>) => void;
  clearSearchFilters: () => void;
  setSearchCenter: (center: { lat: number; lng: number } | null) => void;
  setSearchRadius: (radius: number | null) => void;
  setSearchSessionMetadata: (
    metadata: ProviderSearchSessionMetadata | null
  ) => void;

  // UI state actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setHasMoreResults: (hasMore: boolean) => void;

  // User interaction actions
  toggleSavedProvider: (providerId: number) => void;
  /** Authoritative replace for patient (or SW) backend favorites sync — never use toggle for snapshots. */
  setSavedProviders: (providerIds: Iterable<number>) => void;
  toggleComparedProvider: (providerId: number) => void;
  setComparedProviders: (providerIds: number[]) => void;
  setStagedCompareProviders: (providers: ProviderSearchResult[]) => void;
  clearSavedProviders: () => void;
  clearComparedProviders: () => void;

  // Pagination actions
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (itemsPerPage: number) => void;
  loadMore: () => void;

  // Auto-search actions
  setHasAutoSearched: (hasSearched: boolean) => void;
  setHasSearchedWithZeroResults: (hasSearched: boolean) => void;
  clearAutoSearchFlags: () => void;

  // Utility actions
  reset: () => void;
  resetUserData: () => void; // Legacy alias for a privacy-safe full reset
}

// Initial state
const initialState: ProviderState = {
  allProviders: [],
  filteredProviders: [],
  paginatedProviders: [],
  searchFilters: DEFAULT_SEARCH_FILTERS,
  searchCenter: null,
  searchRadius: null,
  searchSessionMetadata: null,
  loading: false,
  error: null,
  hasMoreResults: false,
  savedProviders: new Set(),
  comparedProviders: new Set(),
  stagedCompareProviders: [],
  currentPage: 1,
  itemsPerPage: PAGINATION_CONFIG.DEFAULT_ITEMS_PER_PAGE,
  hasAutoSearched: false,
  hasSearchedWithZeroResults: false,
};

// Create the store
export const useProviderStore = create<ProviderState & ProviderActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Provider data actions
        setAllProviders: (providers) =>
          set((state) => ({
            ...state,
            allProviders: providers,
          })),

        setFilteredProviders: (providers) =>
          set((state) => {
            const paginatedProviders = sliceCumulativePage(
              providers,
              1,
              state.itemsPerPage
            );
            const hasMoreResults = hasMoreCumulativePage(
              providers.length,
              1,
              state.itemsPerPage
            );

            return {
              ...state,
              filteredProviders: providers,
              paginatedProviders,
              hasMoreResults,
              currentPage: 1,
            };
          }),

        setPaginatedProviders: (providers) =>
          set((state) => ({
            ...state,
            paginatedProviders: providers,
          })),

        // Search actions — normalize at ingress so employment Care Services
        // (and other deconflation rules) always reach the API honestly.
        setSearchFilters: (filters) =>
          set((state) => ({
            ...state,
            searchFilters: normalizeSearchFilters(filters),
          })),

        updateSearchFilter: (key, value) =>
          set((state) => ({
            ...state,
            searchFilters: normalizeSearchFilters({
              ...state.searchFilters,
              [key]: value,
            }),
          })),

        patchSearchFilters: (partial) =>
          set((state) => ({
            ...state,
            searchFilters: normalizeSearchFilters({
              ...state.searchFilters,
              ...partial,
            }),
          })),

        clearSearchFilters: () =>
          set((state) => ({
            ...state,
            allProviders: [],
            searchFilters: { ...DEFAULT_SEARCH_FILTERS },
            searchCenter: null,
            searchRadius: null,
            searchSessionMetadata: null,
            filteredProviders: [],
            paginatedProviders: [],
            currentPage: 1,
            hasMoreResults: false,
            hasAutoSearched: false,
            hasSearchedWithZeroResults: false,
          })),

        setSearchCenter: (center) =>
          set((state) => ({
            ...state,
            searchCenter: center,
          })),

        setSearchRadius: (radius) =>
          set((state) => ({
            ...state,
            searchRadius: radius,
          })),

        setSearchSessionMetadata: (metadata) =>
          set((state) => ({
            ...state,
            searchSessionMetadata: metadata,
          })),

        // UI state actions
        setLoading: (loading) =>
          set((state) => ({
            ...state,
            loading,
          })),

        setError: (error) =>
          set((state) => ({
            ...state,
            error,
          })),

        setHasMoreResults: (hasMore) =>
          set((state) => ({
            ...state,
            hasMoreResults: hasMore,
          })),

        // User interaction actions
        toggleSavedProvider: (providerId) =>
          set((state) => {
            const newSavedProviders = new Set(state.savedProviders);
            if (newSavedProviders.has(providerId)) {
              newSavedProviders.delete(providerId);
            } else {
              // Enforce size limit
              if (
                newSavedProviders.size >= STORAGE_LIMITS.MAX_SAVED_PROVIDERS
              ) {
                const message = `Cannot add more saved providers. Limit reached: ${STORAGE_LIMITS.MAX_SAVED_PROVIDERS}`;
                console.warn(message);

                // Dispatch user-facing notification
                if (typeof window !== "undefined" && window.dispatchEvent) {
                  window.dispatchEvent(
                    new CustomEvent("storageError", {
                      detail: {
                        error: "QUOTA_EXCEEDED",
                        message: `You've reached the maximum number of saved providers (${STORAGE_LIMITS.MAX_SAVED_PROVIDERS}). Please remove some before adding new ones.`,
                        key: "provider-store",
                        operation: "write",
                        userMessage: `You've reached the maximum number of saved providers. Please remove some before adding new ones.`,
                      },
                    })
                  );
                }

                return state; // Don't modify state if limit reached
              }
              newSavedProviders.add(providerId);
            }
            return {
              ...state,
              savedProviders: newSavedProviders,
            };
          }),

        setSavedProviders: (providerIds) =>
          set((state) => {
            const next = new Set<number>();
            let truncated = false;
            for (const rawId of providerIds) {
              if (!Number.isFinite(rawId) || rawId <= 0 || next.has(rawId)) {
                continue;
              }
              if (next.size >= STORAGE_LIMITS.MAX_SAVED_PROVIDERS) {
                truncated = true;
                break;
              }
              next.add(rawId);
            }
            if (truncated) {
              console.warn(
                `setSavedProviders truncated to ${STORAGE_LIMITS.MAX_SAVED_PROVIDERS} IDs (snapshot had more).`
              );
            }
            return {
              ...state,
              savedProviders: next,
            };
          }),

        toggleComparedProvider: (providerId) =>
          set((state) => {
            const newComparedProviders = new Set(state.comparedProviders);
            if (newComparedProviders.has(providerId)) {
              newComparedProviders.delete(providerId);
              return {
                ...state,
                comparedProviders: newComparedProviders,
                stagedCompareProviders: state.stagedCompareProviders.filter(
                  (row) => row.provider.id !== providerId
                ),
              };
            }
            // Enforce both UI limit and storage limit
            const maxCompare = Math.min(
              PAGINATION_CONFIG.MAX_COMPARE_COUNT,
              STORAGE_LIMITS.MAX_COMPARED_PROVIDERS
            );
            if (newComparedProviders.size >= maxCompare) {
              const message = `Cannot add more compared providers. Limit reached: ${maxCompare}`;
              console.warn(message);

              // Dispatch user-facing notification
              if (typeof window !== "undefined" && window.dispatchEvent) {
                window.dispatchEvent(
                  new CustomEvent("storageError", {
                    detail: {
                      error: "QUOTA_EXCEEDED",
                      message: `You've reached the maximum number of compared providers (${maxCompare}). Please remove some before adding new ones.`,
                      key: "provider-store",
                      operation: "write",
                      userMessage: `You've reached the maximum number of compared providers. Please remove some before adding new ones.`,
                    },
                  })
                );
              }

              return state; // Don't modify state if limit reached
            }
            newComparedProviders.add(providerId);
            return {
              ...state,
              comparedProviders: newComparedProviders,
            };
          }),

        clearSavedProviders: () =>
          set((state) => ({
            ...state,
            savedProviders: new Set(),
          })),

        clearComparedProviders: () =>
          set((state) => ({
            ...state,
            comparedProviders: new Set(),
            stagedCompareProviders: [],
          })),

        setComparedProviders: (providerIds) =>
          set((state) => {
            const maxCompare = Math.min(
              PAGINATION_CONFIG.MAX_COMPARE_COUNT,
              STORAGE_LIMITS.MAX_COMPARED_PROVIDERS
            );
            const next = new Set<number>();
            for (const rawId of providerIds) {
              if (!Number.isFinite(rawId) || rawId <= 0 || next.has(rawId)) {
                continue;
              }
              next.add(rawId);
              if (next.size >= maxCompare) {
                break;
              }
            }
            return {
              ...state,
              comparedProviders: next,
              stagedCompareProviders: state.stagedCompareProviders.filter(
                (row) => next.has(row.provider.id)
              ),
            };
          }),

        setStagedCompareProviders: (providers) =>
          set((state) => ({
            ...state,
            stagedCompareProviders: providers,
          })),

        // Pagination actions
        setCurrentPage: (page) =>
          set((state) => {
            const paginatedProviders = sliceCumulativePage(
              state.filteredProviders,
              page,
              state.itemsPerPage
            );
            const hasMoreResults = hasMoreCumulativePage(
              state.filteredProviders.length,
              page,
              state.itemsPerPage
            );

            return {
              ...state,
              currentPage: page,
              paginatedProviders,
              hasMoreResults,
            };
          }),

        setItemsPerPage: (itemsPerPage) =>
          set((state) => {
            const paginatedProviders = sliceCumulativePage(
              state.filteredProviders,
              1,
              itemsPerPage
            );
            const hasMoreResults = hasMoreCumulativePage(
              state.filteredProviders.length,
              1,
              itemsPerPage
            );

            return {
              ...state,
              itemsPerPage,
              currentPage: 1,
              paginatedProviders,
              hasMoreResults,
            };
          }),

        loadMore: () =>
          set((state) => {
            const canLoadMore = hasMoreCumulativePage(
              state.filteredProviders.length,
              state.currentPage,
              state.itemsPerPage
            );
            if (!canLoadMore) {
              return state;
            }

            const nextPage = state.currentPage + 1;
            const paginatedProviders = sliceCumulativePage(
              state.filteredProviders,
              nextPage,
              state.itemsPerPage
            );
            const hasMoreResults = hasMoreCumulativePage(
              state.filteredProviders.length,
              nextPage,
              state.itemsPerPage
            );

            return {
              ...state,
              paginatedProviders,
              currentPage: nextPage,
              hasMoreResults,
            };
          }),

        // Auto-search actions
        setHasAutoSearched: (hasSearched) =>
          set((state) => ({
            ...state,
            hasAutoSearched: hasSearched,
          })),

        setHasSearchedWithZeroResults: (hasSearched) =>
          set((state) => ({
            ...state,
            hasSearchedWithZeroResults: hasSearched,
          })),

        clearAutoSearchFlags: () =>
          set((state) => ({
            ...state,
            hasAutoSearched: false,
            hasSearchedWithZeroResults: false,
          })),

        // Utility actions
        reset: () => set(() => initialState),

        resetUserData: () => set(() => initialState),
      }),
      {
        name: "provider-store",
        storage: createJSONStorage(() => sessionStorage),
        version: 3,
        migrate: (persistedState: unknown, version: number) => {
          const state = persistedState as {
            searchFilters?: SearchFilters;
          };
          if (version < 2 && state?.searchFilters) {
            state.searchFilters = migrateLegacySearchFilters(state.searchFilters);
          }
          return persistedState;
        },
        partialize: (state) => ({
          // Search results are public directory data. Keep the active response
          // session-scoped so route remounts restore the same map/list without
          // relying on the separate time-limited local cache.
          filteredProviders: state.filteredProviders,
          searchCenter: state.searchCenter,
          searchSessionMetadata: state.searchSessionMetadata,
          searchFilters: state.searchFilters,
          savedProviders: Array.from(state.savedProviders),
          comparedProviders: Array.from(state.comparedProviders),
          hasAutoSearched: state.hasAutoSearched,
          hasSearchedWithZeroResults: state.hasSearchedWithZeroResults,
        }),
        onRehydrateStorage: () => (state) => {
          if (state) {
            if (state.searchFilters) {
              state.searchFilters = normalizeSearchFilters(
                migrateLegacySearchFilters(state.searchFilters)
              );
            }
            // Convert arrays back to Sets
            state.savedProviders = new Set(state.savedProviders as any);
            state.comparedProviders = new Set(state.comparedProviders as any);
            if (Array.isArray(state.filteredProviders)) {
              state.allProviders = state.filteredProviders;
              state.paginatedProviders = sliceCumulativePage(
                state.filteredProviders,
                1,
                state.itemsPerPage
              );
              state.currentPage = 1;
              state.hasMoreResults = hasMoreCumulativePage(
                state.filteredProviders.length,
                1,
                state.itemsPerPage
              );
            }
          }
        },
      }
    ),
    {
      name: "provider-store",
    }
  )
);

// Selectors — single useShallow call instead of 17 individual store subscriptions
// (each individual useProviderStore() call registers a separate listener).
export const useProviderSelectors = () =>
  useProviderStore(
    useShallow((state) => ({
      allProviders: state.allProviders,
      filteredProviders: state.filteredProviders,
      paginatedProviders: state.paginatedProviders,
      searchFilters: state.searchFilters,
      searchCenter: state.searchCenter,
      searchRadius: state.searchRadius,
      searchSessionMetadata: state.searchSessionMetadata,
      loading: state.loading,
      error: state.error,
      hasMoreResults: state.hasMoreResults,
      savedProviders: state.savedProviders,
      comparedProviders: state.comparedProviders,
      stagedCompareProviders: state.stagedCompareProviders,
      currentPage: state.currentPage,
      itemsPerPage: state.itemsPerPage,
      hasAutoSearched: state.hasAutoSearched,
      hasSearchedWithZeroResults: state.hasSearchedWithZeroResults,
    }))
  );

// Action selectors
export const useProviderActions = () =>
  useProviderStore(
    useShallow((state) => ({
      setAllProviders: state.setAllProviders,
      setFilteredProviders: state.setFilteredProviders,
      setPaginatedProviders: state.setPaginatedProviders,
      setSearchFilters: state.setSearchFilters,
      updateSearchFilter: state.updateSearchFilter,
      patchSearchFilters: state.patchSearchFilters,
      clearSearchFilters: state.clearSearchFilters,
      setSearchCenter: state.setSearchCenter,
      setSearchRadius: state.setSearchRadius,
      setSearchSessionMetadata: state.setSearchSessionMetadata,
      setLoading: state.setLoading,
      setError: state.setError,
      setHasMoreResults: state.setHasMoreResults,
      toggleSavedProvider: state.toggleSavedProvider,
      setSavedProviders: state.setSavedProviders,
      toggleComparedProvider: state.toggleComparedProvider,
      clearSavedProviders: state.clearSavedProviders,
      clearComparedProviders: state.clearComparedProviders,
      setComparedProviders: state.setComparedProviders,
      setStagedCompareProviders: state.setStagedCompareProviders,
      setCurrentPage: state.setCurrentPage,
      setItemsPerPage: state.setItemsPerPage,
      loadMore: state.loadMore,
      setHasAutoSearched: state.setHasAutoSearched,
      setHasSearchedWithZeroResults: state.setHasSearchedWithZeroResults,
      clearAutoSearchFlags: state.clearAutoSearchFlags,
      reset: state.reset,
      resetUserData: state.resetUserData,
    }))
  );
